import { z } from 'zod'

const HH_MM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/
const HH_MM_MESSAGE = 'Must be HH:mm format'

export const WORK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

export const shiftBreakAllowanceSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  duration: z.number().min(1, 'Duration must be greater than 0'),
  isPaid: z.boolean(),
  isFlexible: z.boolean().optional(),
  scheduledStartTime: z
    .string()
    .optional()
    .refine((s) => !s || s === '' || HH_MM_REGEX.test(s), { message: HH_MM_MESSAGE }),
})

export const customShiftSlotBaseSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  startTime: z
    .string()
    .min(1, 'Start time is required')
    .regex(HH_MM_REGEX, 'Start time must be HH:mm'),
  endTime: z.string().min(1, 'End time is required').regex(HH_MM_REGEX, 'End time must be HH:mm'),
  gracePeriodMinutes: z.number().min(0).optional().default(0),
  maxEarlyClockInMinutes: z.number().min(0).optional().default(10),
  allowClockInPastEnd: z.boolean().optional().default(false),
  lateClockInCutoffMinutes: z.number().min(0).optional().default(0),
  breakAllowances: z.array(shiftBreakAllowanceSchema).optional().default([]),
  color: z.string().optional(),
  description: z.string().optional(),
})

export const customShiftSlotSchema = customShiftSlotBaseSchema.superRefine((data, ctx) => {
  if (data.startTime && data.endTime && data.startTime === data.endTime) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endTime'],
      message: 'End time cannot equal start time',
    })
  }
})

const customShiftSlotFormBaseSchema = z.object({
  name: z.string().optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  gracePeriodMinutes: z.number().min(0).optional(),
  maxEarlyClockInMinutes: z.number().min(0).optional(),
  allowClockInPastEnd: z.boolean().optional(),
  lateClockInCutoffMinutes: z.number().min(0).optional(),
  breakAllowances: z.array(shiftBreakAllowanceSchema).optional(),
  color: z.string().optional(),
  description: z.string().optional(),
})

export const createShiftSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().optional(),
  startTime: z.string().regex(HH_MM_REGEX, 'Start time must be HH:mm'),
  endTime: z.string().regex(HH_MM_REGEX, 'End time must be HH:mm'),
  gracePeriodMinutes: z.number().min(0).optional().default(0),
  maxEarlyClockInMinutes: z.number().min(0).optional().default(10),
  allowClockInPastEnd: z.boolean().optional().default(false),
  lateClockInCutoffMinutes: z.number().min(0).optional().default(0),
  breakAllowances: z.array(shiftBreakAllowanceSchema).optional().default([]),
  isTemplate: z.boolean().optional().default(false),
  workDays: z.array(z.enum(WORK_DAYS)).optional().default([]),
  color: z.string().optional(),
  description: z.string().optional(),
  status: z.enum(['active', 'inactive']).optional().default('active'),
})

/** Partial schema for API update payload. */
export const updateShiftSchema = createShiftSchema.partial()

/** Schema for the edit-shift form: same fields as create but all optional for reset from API. */
export const editShiftFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().optional(),
  startTime: z.string().regex(HH_MM_REGEX, 'Start time must be HH:mm'),
  endTime: z.string().regex(HH_MM_REGEX, 'End time must be HH:mm'),
  gracePeriodMinutes: z.number().min(0).optional(),
  maxEarlyClockInMinutes: z.number().min(0).optional(),
  allowClockInPastEnd: z.boolean().optional(),
  lateClockInCutoffMinutes: z.number().min(0).optional(),
  color: z.string().optional(),
  description: z.string().optional(),
  status: z.enum(['active', 'inactive']).optional(),
})

export const createShiftAssignmentSchema = z
  .object({
    assignmentMode: z.enum(['template', 'custom']).default('custom'),
    shiftId: z.string().optional(),
    userId: z.string().min(1, 'Employee is required'),
    date: z.string().min(1, 'Date is required'),
    status: z.enum(['scheduled', 'confirmed', 'cancelled']).optional().default('scheduled'),
    notes: z.string().optional(),
    customSlot: customShiftSlotFormBaseSchema.optional().default({}),
  })
  .superRefine((data, ctx) => {
    if (data.assignmentMode === 'template' && !data.shiftId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shiftId'],
        message: 'Shift is required',
      })
    }
    if (data.assignmentMode === 'custom') {
      const slot = customShiftSlotSchema.safeParse(data.customSlot ?? {})
      if (!slot.success) {
        slot.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['customSlot', ...issue.path],
          })
        })
      }
    }
  })

export const updateShiftAssignmentSchema = z.object({
  status: z.enum(['scheduled', 'confirmed', 'cancelled']).optional(),
  notes: z.string().optional(),
})

export const bulkAssignShiftSchema = z
  .object({
    assignmentMode: z.enum(['template', 'custom']).default('template'),
    shiftId: z.string().optional(),
    userIds: z.array(z.string()).min(1, 'Select at least one user'),
    dates: z.array(z.string()).min(1, 'Select at least one date'),
    customSlot: customShiftSlotFormBaseSchema.optional().default({}),
  })
  .superRefine((data, ctx) => {
    if (data.assignmentMode === 'template' && !data.shiftId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shiftId'],
        message: 'Shift is required',
      })
    }
    if (data.assignmentMode === 'custom') {
      const slot = customShiftSlotSchema.safeParse(data.customSlot ?? {})
      if (!slot.success) {
        slot.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['customSlot', ...issue.path],
          })
        })
      }
    }
  })

export const cancelShiftAssignmentSchema = z.object({
  cancellationReason: z.string().min(1, 'Cancellation reason is required'),
})

export type CreateShiftFormData = z.infer<typeof createShiftSchema>
export type UpdateShiftFormData = z.infer<typeof updateShiftSchema>
export type EditShiftFormData = z.infer<typeof editShiftFormSchema>
export type CreateShiftAssignmentFormData = z.infer<typeof createShiftAssignmentSchema>
export type UpdateShiftAssignmentFormData = z.infer<typeof updateShiftAssignmentSchema>
export type BulkAssignShiftFormData = z.infer<typeof bulkAssignShiftSchema>
export type CancelShiftAssignmentFormData = z.infer<typeof cancelShiftAssignmentSchema>
